import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ThumbUp as ThumbUpIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiGet } from '../../../utils/httpClientTs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSPDPreworkDetail,
  updateSPDPrework,
  approveSPDPrework,
} from '../../../endpoints/annualReportsTs';
import {
  SPDPreworkForm as SPDPreworkFormType,
  SPDPreworkStatus,
  AdditionalFee,
} from '../../../types/annualReports';
import StateSelector from '../../../Components/StateSelector';
import CountrySelector from '../../../Components/CountrySelector';
import UserSearchModal from '../../../Components/UserSearchModal';

const SPDPreworkForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'complete' | 'approve' | 'send_back' | 'unapprove' | null;
  }>({ open: false, type: null });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showFirstApproverModal, setShowFirstApproverModal] = useState(false);
  const [showSecondApproverModal, setShowSecondApproverModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [editingFields, setEditingFields] = useState<{ [key: string]: string }>(
    {}
  );

  const {
    data: form,
    isLoading,
    error,
  } = useApiGet(['spd-prework-detail', id], () => getSPDPreworkDetail(id!), {
    enabled: !!id,
    retry: 1,
  });

  const [formData, setFormData] = useState<Partial<SPDPreworkFormType>>({});
  const [originalFormData, setOriginalFormData] = useState<
    Partial<SPDPreworkFormType>
  >({});

  // Initialize form data when form loads
  React.useEffect(() => {
    if (form) {
      setFormData(form);
      setOriginalFormData(form);
    }
  }, [form]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => updateSPDPrework(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spd-prework-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['spd-prework-list'] });
      queryClient.invalidateQueries({ queryKey: ['spd-prework-list-check'] });
    },
  });

  const approvalMutation = useMutation({
    mutationFn: (action: 'approve' | 'send_back' | 'unapprove') =>
      approveSPDPrework(id!, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spd-prework-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['spd-prework-list'] });
      queryClient.invalidateQueries({ queryKey: ['spd-prework-list-check'] });
    },
  });

  // Auto-dismiss success alerts after 3 seconds
  React.useEffect(() => {
    if (updateMutation.isSuccess || approvalMutation.isSuccess) {
      setShowSuccessAlert(true);
      const timer = setTimeout(() => {
        setShowSuccessAlert(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [updateMutation.isSuccess, approvalMutation.isSuccess]);

  // Show error alerts
  React.useEffect(() => {
    if (updateMutation.isError || approvalMutation.isError) {
      setShowErrorAlert(true);
    }
  }, [updateMutation.isError, approvalMutation.isError]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValidationErrors([]);
  };

  const handleSaveProgress = async () => {
    try {
      await updateMutation.mutateAsync({
        ...formData,
        mark_complete: false,
      });
      // Update original form data after save
      setOriginalFormData(formData);
    } catch (err: any) {
      console.error('Save failed:', err);
    }
  };

  const handleMarkComplete = async () => {
    try {
      await updateMutation.mutateAsync({
        ...formData,
        mark_complete: true,
      });
      setConfirmDialog({ open: false, type: null });
      // Update original form data after save
      setOriginalFormData(formData);
    } catch (err: any) {
      if (err?.missing_fields) {
        setValidationErrors(err.missing_fields);
      }
      setConfirmDialog({ open: false, type: null });
    }
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!form || !originalFormData) return false;

    // Compare key fields that can be edited
    const fieldsToCheck: (keyof SPDPreworkFormType)[] = [
      'state_dues',
      'remit_to_name',
      'first_approver_id',
      'second_approver_id',
      'check_payable_to',
      'address_1',
      'address_2',
      'city',
      'state_id',
      'postal_code',
      'country_id',
    ];

    // Check simple fields
    for (const field of fieldsToCheck) {
      if (formData[field] !== originalFormData[field]) {
        return true;
      }
    }

    // Check additional_fees array
    const currentFees = formData.additional_fees || [];
    const originalFees = originalFormData.additional_fees || [];

    if (currentFees.length !== originalFees.length) {
      return true;
    }

    // Deep compare fees
    for (let i = 0; i < currentFees.length; i++) {
      const current = currentFees[i];
      const original = originalFees[i];

      if (
        current.id !== original.id ||
        current.order !== original.order ||
        current.description !== original.description ||
        current.per_member_fee !== original.per_member_fee ||
        current.per_chapter_fee !== original.per_chapter_fee ||
        (current as any).delete !== undefined
      ) {
        return true;
      }
    }

    return false;
  };

  const handleApprove = async () => {
    try {
      // If there are unsaved changes, save them first
      if (hasUnsavedChanges()) {
        await updateMutation.mutateAsync({
          ...formData,
          mark_complete: false,
        });
        // Update original form data after save
        setOriginalFormData(formData);
      }

      // Then approve
      await approvalMutation.mutateAsync('approve');
      setConfirmDialog({ open: false, type: null });
    } catch (err) {
      console.error('Approve failed:', err);
      setConfirmDialog({ open: false, type: null });
    }
  };

  const handleSendBack = async () => {
    try {
      await approvalMutation.mutateAsync('send_back');
      setConfirmDialog({ open: false, type: null });
    } catch (err) {
      console.error('Send back failed:', err);
      setConfirmDialog({ open: false, type: null });
    }
  };

  const handleUnapprove = async () => {
    try {
      await approvalMutation.mutateAsync('unapprove');
      setConfirmDialog({ open: false, type: null });
    } catch (err) {
      console.error('Unapprove failed:', err);
      setConfirmDialog({ open: false, type: null });
    }
  };

  const handleAddFee = () => {
    const newFee: AdditionalFee = {
      id: null,
      order: (formData.additional_fees?.length || 0) + 1,
      description: '',
      per_member_fee: null,
      per_chapter_fee: null,
    };
    setFormData((prev) => ({
      ...prev,
      additional_fees: [...(prev.additional_fees || []), newFee],
    }));
  };

  const handleUpdateFee = (index: number, field: string, value: any) => {
    const updatedFees = [...(formData.additional_fees || [])];
    updatedFees[index] = { ...updatedFees[index], [field]: value };
    setFormData((prev) => ({ ...prev, additional_fees: updatedFees }));
  };

  const handleDeleteFee = (index: number) => {
    const updatedFees = [...(formData.additional_fees || [])];
    if (updatedFees[index].id) {
      // Mark existing fee for deletion
      updatedFees[index] = { ...updatedFees[index], delete: true };
    } else {
      // Remove new fee that hasn't been saved yet
      updatedFees.splice(index, 1);
    }
    setFormData((prev) => ({ ...prev, additional_fees: updatedFees }));
  };

  const getStatusColor = (statusId: string) => {
    switch (statusId) {
      case SPDPreworkStatus.NOT_STARTED:
        return 'default';
      case SPDPreworkStatus.IN_PROGRESS:
        return 'warning';
      case SPDPreworkStatus.READY_FOR_REVIEW:
        return 'info';
      case SPDPreworkStatus.APPROVED:
        return 'success';
      default:
        return 'default';
    }
  };

  // Check if user has approval permissions (Admin, Support, Finance)
  const hasApprovalPermissions = [
    '785238dd501e4544b3970f742b19b04a', // Admin
    '04a1d0a6f0884bcfb44911c2eaa5b77f', // Support
    '21160e65fd7243c49023aad41be8b084', // Finance
  ].includes(localStorage.getItem('role') || '');

  // Form is editable if:
  // - Not approved, AND
  // - If status is "Ready for Review", user must have approval permissions
  const isEditable =
    form?.status_id !== SPDPreworkStatus.APPROVED &&
    (form?.status_id !== SPDPreworkStatus.READY_FOR_REVIEW ||
      hasApprovalPermissions);

  const canUnapprove =
    form?.status_id === SPDPreworkStatus.APPROVED && hasApprovalPermissions;

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading form. Please try again.</Alert>
      </Box>
    );
  }

  if (isLoading || !form) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box sx={{ p: 3, pb: 8 }}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/annual-reports/prework')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            {form.spd_chapter_name} ({form.spd_chapter_abbreviation})
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Year: {form.year}
            </Typography>
            <Chip
              label={form.status_name}
              color={getStatusColor(form.status_id) as any}
              size="small"
            />
            {form.submitted_at && (
              <Typography variant="body2" color="text.secondary">
                Submitted: {new Date(form.submitted_at).toLocaleDateString()}
                {form.submitted_by_name && ` by ${form.submitted_by_name}`}
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>

      {/* Basic Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="State Dues *"
                value={
                  editingFields['state_dues'] !== undefined
                    ? editingFields['state_dues']
                    : formData.state_dues === null ||
                        formData.state_dues === undefined
                      ? ''
                      : formData.state_dues.toFixed(2)
                }
                onFocus={() => {
                  const currentValue =
                    formData.state_dues === null ||
                    formData.state_dues === undefined
                      ? ''
                      : formData.state_dues.toString();
                  setEditingFields((prev) => ({
                    ...prev,
                    state_dues: currentValue,
                  }));
                }}
                onChange={(e) => {
                  const inputValue = e.target.value.replace(/[^0-9.]/g, '');

                  // Allow empty or valid decimal numbers
                  if (inputValue === '' || /^\d*\.?\d{0,2}$/.test(inputValue)) {
                    setEditingFields((prev) => ({
                      ...prev,
                      state_dues: inputValue,
                    }));

                    const numValue =
                      inputValue === '' ? null : parseFloat(inputValue);
                    handleFieldChange(
                      'state_dues',
                      isNaN(numValue) || numValue === null ? null : numValue
                    );
                  }
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value;

                  if (inputValue && inputValue !== '') {
                    const value = parseFloat(inputValue);
                    if (!isNaN(value) && value >= 0) {
                      handleFieldChange(
                        'state_dues',
                        parseFloat(value.toFixed(2))
                      );
                    } else {
                      handleFieldChange('state_dues', null);
                    }
                  } else {
                    handleFieldChange('state_dues', null);
                  }

                  // Clear editing state
                  setEditingFields((prev) => {
                    const newState = { ...prev };
                    delete newState['state_dues'];
                    return newState;
                  });
                }}
                disabled={!isEditable}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Additional Fees */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" fontWeight={600}>
              Additional Fees
            </Typography>
            {isEditable && (
              <>
                <Tooltip title="Add Fee">
                  <IconButton
                    onClick={handleAddFee}
                    color="primary"
                    size="small"
                    sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddFee}
                  variant="outlined"
                  size="small"
                  sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                >
                  Add Fee
                </Button>
              </>
            )}
          </Stack>
          <Divider sx={{ mb: 3 }} />
          {formData.additional_fees && formData.additional_fees.length > 0 ? (
            <Paper variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Per Member Fee</TableCell>
                    <TableCell>Per Chapter Fee</TableCell>
                    {isEditable && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.additional_fees
                    .filter((fee) => !fee.delete)
                    .map((fee, index) => (
                      <TableRow key={index}>
                        <TableCell>{fee.order}</TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={fee.description}
                            onChange={(e) =>
                              handleUpdateFee(
                                index,
                                'description',
                                e.target.value
                              )
                            }
                            disabled={!isEditable}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={
                              editingFields[`per_member_${index}`] !== undefined
                                ? editingFields[`per_member_${index}`]
                                : fee.per_member_fee === null
                                  ? ''
                                  : fee.per_member_fee.toFixed(2)
                            }
                            onFocus={() => {
                              const currentValue =
                                fee.per_member_fee === null
                                  ? ''
                                  : fee.per_member_fee.toString();
                              setEditingFields((prev) => ({
                                ...prev,
                                [`per_member_${index}`]: currentValue,
                              }));
                            }}
                            onChange={(e) => {
                              const inputValue = e.target.value.replace(
                                /[^0-9.]/g,
                                ''
                              );

                              // Allow empty or valid decimal numbers
                              if (
                                inputValue === '' ||
                                /^\d*\.?\d{0,2}$/.test(inputValue)
                              ) {
                                setEditingFields((prev) => ({
                                  ...prev,
                                  [`per_member_${index}`]: inputValue,
                                }));

                                const updatedFees = [
                                  ...(formData.additional_fees || []),
                                ];

                                const numValue =
                                  inputValue === ''
                                    ? null
                                    : parseFloat(inputValue);

                                updatedFees[index] = {
                                  ...updatedFees[index],
                                  per_member_fee:
                                    isNaN(numValue) || numValue === null
                                      ? null
                                      : numValue,
                                  // Clear per_chapter_fee if this has a value
                                  per_chapter_fee:
                                    numValue !== null && numValue > 0
                                      ? null
                                      : updatedFees[index].per_chapter_fee,
                                };
                                setFormData((prev) => ({
                                  ...prev,
                                  additional_fees: updatedFees,
                                }));
                              }
                            }}
                            onBlur={(e) => {
                              const inputValue = e.target.value;
                              const updatedFees = [
                                ...(formData.additional_fees || []),
                              ];

                              if (inputValue && inputValue !== '') {
                                const value = parseFloat(inputValue);
                                if (!isNaN(value) && value >= 0) {
                                  updatedFees[index] = {
                                    ...updatedFees[index],
                                    per_member_fee: parseFloat(
                                      value.toFixed(2)
                                    ),
                                  };
                                } else {
                                  updatedFees[index] = {
                                    ...updatedFees[index],
                                    per_member_fee: null,
                                  };
                                }
                              } else {
                                updatedFees[index] = {
                                  ...updatedFees[index],
                                  per_member_fee: null,
                                };
                              }

                              setFormData((prev) => ({
                                ...prev,
                                additional_fees: updatedFees,
                              }));

                              // Clear editing state
                              setEditingFields((prev) => {
                                const newState = { ...prev };
                                delete newState[`per_member_${index}`];
                                return newState;
                              });
                            }}
                            disabled={
                              !isEditable ||
                              (fee.per_chapter_fee !== null &&
                                fee.per_chapter_fee !== 0)
                            }
                            placeholder={
                              fee.per_chapter_fee && fee.per_chapter_fee > 0
                                ? 'N/A'
                                : '0.00'
                            }
                            InputProps={{
                              startAdornment: (
                                <Typography sx={{ mr: 0.5 }}>$</Typography>
                              ),
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={
                              editingFields[`per_chapter_${index}`] !==
                              undefined
                                ? editingFields[`per_chapter_${index}`]
                                : fee.per_chapter_fee === null
                                  ? ''
                                  : fee.per_chapter_fee.toFixed(2)
                            }
                            onFocus={() => {
                              const currentValue =
                                fee.per_chapter_fee === null
                                  ? ''
                                  : fee.per_chapter_fee.toString();
                              setEditingFields((prev) => ({
                                ...prev,
                                [`per_chapter_${index}`]: currentValue,
                              }));
                            }}
                            onChange={(e) => {
                              const inputValue = e.target.value.replace(
                                /[^0-9.]/g,
                                ''
                              );

                              // Allow empty or valid decimal numbers
                              if (
                                inputValue === '' ||
                                /^\d*\.?\d{0,2}$/.test(inputValue)
                              ) {
                                setEditingFields((prev) => ({
                                  ...prev,
                                  [`per_chapter_${index}`]: inputValue,
                                }));

                                const updatedFees = [
                                  ...(formData.additional_fees || []),
                                ];

                                const numValue =
                                  inputValue === ''
                                    ? null
                                    : parseFloat(inputValue);

                                updatedFees[index] = {
                                  ...updatedFees[index],
                                  per_chapter_fee:
                                    isNaN(numValue) || numValue === null
                                      ? null
                                      : numValue,
                                  // Clear per_member_fee if this has a value
                                  per_member_fee:
                                    numValue !== null && numValue > 0
                                      ? null
                                      : updatedFees[index].per_member_fee,
                                };
                                setFormData((prev) => ({
                                  ...prev,
                                  additional_fees: updatedFees,
                                }));
                              }
                            }}
                            onBlur={(e) => {
                              const inputValue = e.target.value;
                              const updatedFees = [
                                ...(formData.additional_fees || []),
                              ];

                              if (inputValue && inputValue !== '') {
                                const value = parseFloat(inputValue);
                                if (!isNaN(value) && value >= 0) {
                                  updatedFees[index] = {
                                    ...updatedFees[index],
                                    per_chapter_fee: parseFloat(
                                      value.toFixed(2)
                                    ),
                                  };
                                } else {
                                  updatedFees[index] = {
                                    ...updatedFees[index],
                                    per_chapter_fee: null,
                                  };
                                }
                              } else {
                                updatedFees[index] = {
                                  ...updatedFees[index],
                                  per_chapter_fee: null,
                                };
                              }

                              setFormData((prev) => ({
                                ...prev,
                                additional_fees: updatedFees,
                              }));

                              // Clear editing state
                              setEditingFields((prev) => {
                                const newState = { ...prev };
                                delete newState[`per_chapter_${index}`];
                                return newState;
                              });
                            }}
                            disabled={
                              !isEditable ||
                              (fee.per_member_fee !== null &&
                                fee.per_member_fee !== 0)
                            }
                            placeholder={
                              fee.per_member_fee && fee.per_member_fee > 0
                                ? 'N/A'
                                : '0.00'
                            }
                            InputProps={{
                              startAdornment: (
                                <Typography sx={{ mr: 0.5 }}>$</Typography>
                              ),
                            }}
                          />
                        </TableCell>
                        {isEditable && (
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteFee(index)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No additional fees added yet
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Approvers */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Annual Report Approvers
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Treasurer Role *"
                value={formData.first_approver_name || 'Not selected'}
                disabled
                placeholder="Click Select to choose"
                InputProps={{
                  endAdornment: isEditable ? (
                    <Button
                      size="small"
                      onClick={() => setShowFirstApproverModal(true)}
                      variant="outlined"
                    >
                      {formData.first_approver_id ? 'Change' : 'Select'}
                    </Button>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="President Role *"
                value={formData.second_approver_name || 'Not selected'}
                disabled
                placeholder="Click Select to choose"
                InputProps={{
                  endAdornment: isEditable ? (
                    <Button
                      size="small"
                      onClick={() => setShowSecondApproverModal(true)}
                      variant="outlined"
                    >
                      {formData.second_approver_id ? 'Change' : 'Select'}
                    </Button>
                  ) : null,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Check Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Check Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Make Check Payable To *"
                value={formData.check_payable_to || ''}
                onChange={(e) =>
                  handleFieldChange('check_payable_to', e.target.value)
                }
                disabled={!isEditable}
                placeholder="Enter the name to make checks payable to"
                helperText="This is the name that will appear on checks sent to your chapter"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Address Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Remit To Address
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remit To Name *"
                value={formData.remit_to_name || ''}
                onChange={(e) =>
                  handleFieldChange('remit_to_name', e.target.value)
                }
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1 *"
                value={formData.address_1 || ''}
                onChange={(e) => handleFieldChange('address_1', e.target.value)}
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.address_2 || ''}
                onChange={(e) => handleFieldChange('address_2', e.target.value)}
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City *"
                value={formData.city || ''}
                onChange={(e) => handleFieldChange('city', e.target.value)}
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StateSelector
                value={formData.state_id || ''}
                handleChange={(value) =>
                  handleFieldChange('state_id', value?.id || null)
                }
                countryId={formData.country_id}
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Postal Code *"
                value={formData.postal_code || ''}
                onChange={(e) =>
                  handleFieldChange('postal_code', e.target.value)
                }
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CountrySelector
                value={formData.country_id || ''}
                handleChange={(value) =>
                  handleFieldChange('country_id', value?.id || null)
                }
                disabled={!isEditable}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card>
        <CardContent>
          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Missing required fields:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationErrors.map((field) => (
                  <li key={field}>{field.replace(/_/g, ' ')}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Success Messages */}
          {showSuccessAlert &&
            (updateMutation.isSuccess || approvalMutation.isSuccess) && (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                onClose={() => setShowSuccessAlert(false)}
              >
                {updateMutation.isSuccess && 'Form saved successfully!'}
                {approvalMutation.isSuccess && 'Action completed successfully!'}
              </Alert>
            )}
          {/* Error Messages */}
          {showErrorAlert && updateMutation.isError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setShowErrorAlert(false)}
            >
              Failed to save form. Please try again.
            </Alert>
          )}
          {showErrorAlert && approvalMutation.isError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setShowErrorAlert(false)}
            >
              Failed to complete action. Please try again.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {(form?.status_id === SPDPreworkStatus.NOT_STARTED ||
              form?.status_id === SPDPreworkStatus.IN_PROGRESS) && (
              <>
                <Tooltip title="Save Progress">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={handleSaveProgress}
                      disabled={updateMutation.isPending}
                      sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                    >
                      <SaveIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveProgress}
                  disabled={updateMutation.isPending}
                  sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                >
                  Save Progress
                </Button>
                <Tooltip title="Mark Complete">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() =>
                        setConfirmDialog({ open: true, type: 'complete' })
                      }
                      disabled={updateMutation.isPending}
                      sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                    >
                      <CheckCircleIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={() =>
                    setConfirmDialog({ open: true, type: 'complete' })
                  }
                  disabled={updateMutation.isPending}
                  sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                >
                  Mark Complete
                </Button>
              </>
            )}
            {form?.status_id === SPDPreworkStatus.READY_FOR_REVIEW && (
              <>
                {hasApprovalPermissions ? (
                  <>
                    <Tooltip
                      title={hasUnsavedChanges() ? 'Save & Approve' : 'Approve'}
                    >
                      <span>
                        <IconButton
                          color="success"
                          onClick={() =>
                            setConfirmDialog({ open: true, type: 'approve' })
                          }
                          disabled={
                            approvalMutation.isPending ||
                            updateMutation.isPending
                          }
                          sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                        >
                          <ThumbUpIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ThumbUpIcon />}
                      onClick={() =>
                        setConfirmDialog({ open: true, type: 'approve' })
                      }
                      disabled={
                        approvalMutation.isPending || updateMutation.isPending
                      }
                      sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                    >
                      {hasUnsavedChanges() ? 'Save & Approve' : 'Approve'}
                    </Button>
                    <Tooltip title="Send Back">
                      <span>
                        <IconButton
                          color="warning"
                          onClick={() =>
                            setConfirmDialog({ open: true, type: 'send_back' })
                          }
                          disabled={approvalMutation.isPending}
                          sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                        >
                          <UndoIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<UndoIcon />}
                      onClick={() =>
                        setConfirmDialog({ open: true, type: 'send_back' })
                      }
                      disabled={approvalMutation.isPending}
                      sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                    >
                      Send Back
                    </Button>
                  </>
                ) : (
                  <Alert severity="info" sx={{ width: '100%' }}>
                    This form is awaiting approval from an administrator. It
                    cannot be edited until it is approved or sent back.
                  </Alert>
                )}
              </>
            )}
            {form?.status_id === SPDPreworkStatus.APPROVED && (
              <>
                {canUnapprove ? (
                  <>
                    <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                      This form has been approved and is now read-only.
                    </Alert>
                    <Tooltip title="Unapprove">
                      <span>
                        <IconButton
                          color="warning"
                          onClick={() =>
                            setConfirmDialog({ open: true, type: 'unapprove' })
                          }
                          disabled={approvalMutation.isPending}
                          sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                        >
                          <UndoIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<UndoIcon />}
                      onClick={() =>
                        setConfirmDialog({ open: true, type: 'unapprove' })
                      }
                      disabled={approvalMutation.isPending}
                      sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                    >
                      Unapprove
                    </Button>
                  </>
                ) : (
                  <Alert severity="success" sx={{ width: '100%' }}>
                    This form has been approved and is now read-only.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: null })}
      >
        <DialogTitle>
          {confirmDialog.type === 'complete' && 'Mark Complete?'}
          {confirmDialog.type === 'approve' && 'Approve Form?'}
          {confirmDialog.type === 'send_back' && 'Send Back Form?'}
          {confirmDialog.type === 'unapprove' && 'Unapprove Form?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.type === 'complete' &&
              'Are you sure you want to mark this form as complete? It will be submitted for review.'}
            {confirmDialog.type === 'approve' &&
              (hasUnsavedChanges()
                ? 'This will save your changes and approve this form. Are you sure?'
                : 'Are you sure you want to approve this form?')}
            {confirmDialog.type === 'send_back' &&
              'Are you sure you want to send this form back? The submitter will be notified.'}
            {confirmDialog.type === 'unapprove' &&
              'Are you sure you want to unapprove this form? It will return to "Ready for Review" status and can be edited again.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: null })}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.type === 'complete') handleMarkComplete();
              if (confirmDialog.type === 'approve') handleApprove();
              if (confirmDialog.type === 'send_back') handleSendBack();
              if (confirmDialog.type === 'unapprove') handleUnapprove();
            }}
            variant="contained"
            color={
              confirmDialog.type === 'send_back' ||
              confirmDialog.type === 'unapprove'
                ? 'warning'
                : 'primary'
            }
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Search Modals */}
      <UserSearchModal
        modalOpen={showFirstApproverModal}
        setModalOpen={setShowFirstApproverModal}
        setUser={(user) => {
          handleFieldChange('first_approver_id', user.id);
          handleFieldChange('first_approver_name', user.full_name);
          setShowFirstApproverModal(false);
        }}
        chapter={null}
        spd={null}
        activeMembers={false}
        userObjectRet={true}
      />
      <UserSearchModal
        modalOpen={showSecondApproverModal}
        setModalOpen={setShowSecondApproverModal}
        setUser={(user) => {
          handleFieldChange('second_approver_id', user.id);
          handleFieldChange('second_approver_name', user.full_name);
          setShowSecondApproverModal(false);
        }}
        chapter={null}
        spd={null}
        activeMembers={false}
        userObjectRet={true}
      />
    </Box>
  );
};

export default SPDPreworkForm;
